import React, { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend
} from 'recharts';
import './DoctorDashboard.css';
import { TranslationContext } from '../hooks/TranslationProvider';
import Appointments from '../components/Appointments';
import VideoCallModal from '../components/VideoCallModal';
import SessionModal from '../components/SessionRecordingModal';
import ReportModal from '../components/SessionReportModal';
import { createWebSocketConnection } from '../utils/webrtcConfig';
import { authFetch, authHeaders } from '../utils/authFetch';
import {
  doctorListSessions,
  doctorListSessionNotes,
  doctorListPatients,
  doctorCreatePatient,
  doctorDeletePatient,
  doctorUpdatePatient,
} from '../components/api';
import { exportToCSV } from '../utils/dashboardSharedUtils';
import AppShell from '../components/shell/AppShell';
import { LuLayoutDashboard, LuUsers, LuCalendarDays, LuPlus } from 'react-icons/lu';

// Risk calculation functions
function calculateCumulativeRisk(components) {
  return (
    0.4 * components.emotional_anomaly +
    0.3 * components.behavioral_deviation +
    0.2 * components.burnout +
    0.1 * components.intent_score
  );
}

function getHealthStatus(burnout, cumulative) {
  if (cumulative > 75 || burnout > 70) return "High Risk";
  if (cumulative > 50 || burnout > 50) return "Moderate Risk";
  return "Stable";
}

function checkHighRisk(predictions) {
  if (predictions.suicide_risk > 75 || predictions.violence_risk > 75) {
    return {
      status: true,
      message: "High Risk Future Warning Triggered",
      details: predictions
    };
  }
  return { status: false };
}

function loadPatientDashboard(patient) {
  // Handle new patients without session data
  if (!patient.hasSessionData) {
    return {
      ...patient,
      cumulativeRisk: 0,
      healthStatus: 'No Data',
      highRiskWarning: { status: false }
    };
  }

  const cumulative = calculateCumulativeRisk(patient.riskComponents);
  const healthStatus = getHealthStatus(patient.riskComponents.burnout, cumulative);
  const highRiskFlag = checkHighRisk(patient.predictions);

  return {
    ...patient,
    cumulativeRisk: cumulative,
    healthStatus,
    highRiskWarning: highRiskFlag
  };
}

export default function DoctorDashboard() {
  const { t } = useContext(TranslationContext);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorId, setDoctorId] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionPatient, setSessionPatient] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [, setSessionNote] = useState('');
  const [, setAnalyzedEmotion] = useState(null);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [latestSessionNote, setLatestSessionNote] = useState(null);
  const [selectedSessionDetails, setSelectedSessionDetails] = useState(null);
  const [showSessionDetailsModal, setShowSessionDetailsModal] = useState(false);
  const [sessionNotes, setSessionNotes] = useState([]);
  // Real dashboard stats and growth data (computed from sessions)
  const [dashboardStats, setDashboardStats] = useState({
    totalPatients: 0,
    highRiskPatients: 0,
    todaySessions: 0,
    weeklyGrowth: 0,
    monthlyGrowth: 0
  });

  const [growthData, setGrowthData] = useState({
    daily: [],
    weekly: [],
    monthly: []
  });

  const exportPatientsToCSV = useCallback(() => {
    if (!patients.length) {
      alert(t('No patients to export'));
      return;
    }

    const headers = [
      'Name',
      'Age',
      'Address',
      'Contact',
      'Last Session',
      'Next Session',
      'Overall Mark',
      'Mental Health Status',
      'Risk Score'
    ];

    const rows = patients.map(p => [
      p.name || '',
      p.age ?? '',
      p.address || '',
      p.contact || '',
      p.lastSession || '',
      p.nextSession || '',
      p.overallMark ?? '',
      p.lastMentalHealth || '',
      p.riskComponents?.burnout ?? ''
    ]);

    exportToCSV(headers, rows, 'patients_export');
  }, [patients, t]);

  const [addForm, setAddForm] = useState({
    name: '',
    age: '',
    address: '',
    contact: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    age: '',
    address: '',
    contact: ''
  });

  // Track new appointments (unread notifications)
  const [newAppointmentIds, setNewAppointmentIds] = useState(new Set());

  // Load doctor ID and patients on mount
  useEffect(() => {
    const id = localStorage.getItem('neuro_user_id');
    setDoctorId(id);
    if (id) {
      loadPatients(id);
    }
  }, []);

  // WebSocket listener for new appointment notifications (always connected)
  useEffect(() => {
    if (!doctorId) return;

    let userWs = null;
    let reconnectInterval = null;

    const connectWebSocket = async () => {
      try {
        userWs = await createWebSocketConnection(`user/${doctorId}`);
        
        userWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'new_appointment') {
              const appointmentId = String(data.appointment_id);
              
              // Mark appointment as new/unread
              setNewAppointmentIds(prev => {
                const next = new Set([...prev, appointmentId]);
                return next;
              });
              
              // Show browser notification if permission granted
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('New Appointment', {
                  body: `${data.patient_name} booked an appointment for ${data.date} at ${data.time}`,
                  icon: '/favicon.ico'
                });
              }
            }
          } catch (e) {
            console.warn('❌ [DoctorDashboard] Failed to parse WebSocket message:', e, event.data);
          }
        };

        userWs.onclose = () => {
          if (!reconnectInterval) {
            reconnectInterval = setInterval(connectWebSocket, 5000);
          }
        };

        userWs.onerror = (error) => {
          console.error('❌ [DoctorDashboard] Appointments WebSocket error:', error);
          userWs?.close();
        };

        if (reconnectInterval) {
          clearInterval(reconnectInterval);
          reconnectInterval = null;
        }
      } catch (e) {
        console.error('❌ [DoctorDashboard] Failed to connect appointments WebSocket:', e);
        if (!reconnectInterval) {
          reconnectInterval = setInterval(connectWebSocket, 5000);
        }
      }
    };

    // Request notification permission on mount
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    connectWebSocket();

    return () => {
      if (userWs) {
        try { userWs.close(); } catch { }
      }
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
    };
  }, [doctorId]);

  // Auto-refresh patients periodically when Patients tab is active
  useEffect(() => {
    if (!doctorId) return;
    if (activeTab !== 'patients') return;
    const intervalId = setInterval(() => {
      loadPatients(doctorId).catch(() => {});
    }, 60000);
    return () => clearInterval(intervalId);
  }, [doctorId, activeTab]);

  const loadSessionHistory = async (patient_id) => {
    try {
      const response = await doctorListSessions(patient_id);
      if (response.status_code === 200) {
        const sessions = response.data || [];
        setSessionHistory(sessions);
        // fetch latest note for the most recent session
        if (sessions.length > 0) {
          try {
            const latestId = sessions[0].session_id;
            const notesRes = await doctorListSessionNotes(latestId);
            if (notesRes && notesRes.status_code === 200 && Array.isArray(notesRes.data) && notesRes.data.length > 0) {
              setLatestSessionNote(notesRes.data[0].note_text || null);
            } else {
              setLatestSessionNote(null);
            }
          } catch (_) {
            setLatestSessionNote(null);
          }
        } else {
          setLatestSessionNote(null);
        }
      } else {
        setSessionHistory([]);
        setLatestSessionNote(null);
      }
    } catch (error) {
      console.error('Error loading session history:', error);
      setSessionHistory([]);
      setLatestSessionNote(null);
    }
  };

  const loadSessionNotes = async (session_id) => {
    try {
      const response = await doctorListSessionNotes(session_id);
      if (response.status_code === 200) {
        setSessionNotes(response.data || []);
      } else {
        setSessionNotes([]);
      }
    } catch (error) {
      console.error('Error loading session notes:', error);
      setSessionNotes([]);
    }
  };

  const loadPatients = async (doctor_id) => {
    try {
      setLoading(true);
      const response = await doctorListPatients(doctor_id);

      // --- Load all appointments for this doctor (for Next/Last session logic) ---
      let allAppointmentsRaw = [];
      try {
        const base = process.env.REACT_APP_API_URL || '/api';
        const aptRes = await authFetch(`${base}/doctor/appointments?doctor_id=${doctor_id}`, { headers: authHeaders() });
        const aptJson = await aptRes.json();
        if (aptJson && aptJson.status_code === 200 && Array.isArray(aptJson.data)) {
          allAppointmentsRaw = aptJson.data.map((apt) => ({
            id: apt.id,
            patient_id: apt.patient_id,
            date: apt.date, // YYYY-MM-DD
            time: apt.time, // HH:mm
            purpose: apt.purpose,
            status: apt.status,
            dt: (() => {
              try { return new Date(`${apt.date}T${apt.time}:00`); } catch (_) { return null; }
            })()
          })).filter(a => !!a.dt);
        }
      } catch (err) {
        console.warn('⚠️ Unable to load appointments', err);
      }

      const now = new Date();
      if (response.status_code === 200) {
        // Transform DB data to match frontend format and fetch session data
        const allSessions = []; // collect all sessions across patients
        const transformedPatients = await Promise.all(response.data.map(async (patient) => {
          // Initialize with null/empty data for new patients
          let sessionData = {
            emotionVector: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0 },
            weeklyReport: [],
            monthlyReport: [],
            predictions: { suicide_risk: 0, violence_risk: 0 },
            riskComponents: {
              emotional_anomaly: 0,
              behavioral_deviation: 0,
              burnout: 0,
              intent_score: 0
            },
            overallMark: 0,
            lastMentalHealth: 'No Data',
            hasSessionData: false
          };

          try {
            // Fetch session data for this patient with retry logic
            let sessionsResponse;
            let retryCount = 0;
            const maxRetries = 2;

            while (retryCount <= maxRetries) {
              try {
                sessionsResponse = await doctorListSessions(patient.id);
                break; // Success, exit retry loop
              } catch (error) {
                if (retryCount < maxRetries && (
                  error.message.includes('ERR_CONNECTION_RESET') ||
                  error.message.includes('Network Error') ||
                  error.message.includes('Failed to fetch')
                )) {
                  // eslint-disable-next-line no-loop-func
                  await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                  retryCount++;
                } else {
                  throw error; // Re-throw if max retries reached or different error
                }
              }
            }

            if (sessionsResponse && sessionsResponse.status_code === 200 && sessionsResponse.data.length > 0) {
              const sessions = sessionsResponse.data;

              // Mark that this patient has session data
              sessionData.hasSessionData = true;

              // Collect sessions for global dashboard charts
              try {
                sessions.forEach(s => {
                  if (s && s.session_date) allSessions.push({ date: new Date(s.session_date), patientId: patient.id });
                });
              } catch (_) { }

              // Calculate emotion vector from latest session
              const latestSession = sessions[0];
              if (latestSession.emotion) {
                const emotions = latestSession.emotion;
                sessionData.emotionVector = {
                  joy: emotions.joy || 0,
                  stress: emotions.stress || 0,
                  sadness: emotions.sadness || 0,
                  anger: emotions.anger || 0,
                  hurt: emotions.hurt || 0
                };

                // Calculate burnout from emotions (robust to missing fields)
                const toNum = (v) => (Number.isFinite(v) ? v : 0);
                const burnout = (emotions.burnout_score ?? Math.round((toNum(emotions.stress) + toNum(emotions.sadness) + toNum(emotions.anger)) / 3));

                // Calculate overall mark (inverse of burnout)
                sessionData.overallMark = Math.max(0, 100 - burnout);

                // Determine mental health status based on actual data
                if (burnout > 70) {
                  sessionData.lastMentalHealth = 'High Risk';
                } else if (burnout > 50) {
                  sessionData.lastMentalHealth = 'Moderate Risk';
                } else {
                  sessionData.lastMentalHealth = 'Stable';
                }

                // Calculate risk components based on actual emotion data
                // Inputs are already 0-100 percentages; compute simple averages and clamp to 0-100
                const emotionalAnomaly = Math.max(0, Math.min(100, Math.round((toNum(emotions.stress) + toNum(emotions.sadness) + toNum(emotions.anger)) / 3)));
                const behavioralDeviation = Math.max(0, Math.min(100, Math.round((toNum(emotions.anger) + toNum(emotions.hurt)) / 2)));
                const intentScore = Math.max(0, Math.min(100, Math.round((toNum(emotions.sadness) + toNum(emotions.hurt)) / 2)));
                sessionData.riskComponents = {
                  emotional_anomaly: emotionalAnomaly,
                  behavioral_deviation: behavioralDeviation,
                  burnout: Math.max(0, Math.min(100, Math.round(burnout))),
                  intent_score: intentScore
                };

                // Calculate predictions based on current emotional state
                sessionData.predictions = {
                  suicide_risk: Math.min(100, Math.round((emotions.sadness * 0.6 + emotions.hurt * 0.4) * 100)),
                  violence_risk: Math.min(100, Math.round((emotions.anger * 0.7 + emotions.stress * 0.3) * 100))
                };
              }

              // --- FIX: Ensure Sudden last session time uses local timestamp from latest session ---
              try {
                const latest = latestSession;
                let fixedLastSession = patient.lastSession || '';
                let fixedLastSessionTime = patient.lastSessionTime || '';
                if (latest && latest.session_date) {
                  const latestDateStr = new Date(latest.session_date).toISOString().slice(0, 10);
                  if (fixedLastSession === latestDateStr && (fixedLastSessionTime || '').includes('Sudden')) {
                    const localTime = new Date(latest.session_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    fixedLastSessionTime = `${localTime} (Sudden)`;
                    // overwrite on patient object so downstream render uses corrected time
                    patient = { ...patient, lastSession: fixedLastSession, lastSessionTime: fixedLastSessionTime };
                  }
                }
              } catch (_) { }

              // --- New Logic: Next Session from appointments; Last Session from history ---
              try {
                // Next Session strictly from appointments
                const allForPatient = allAppointmentsRaw.filter(a => a.patient_id === patient.id);
                const nextApt = [...allForPatient].filter(a => a.dt > now).sort((a, b) => a.dt - b.dt)[0] || null;
                patient = nextApt ? { ...patient, nextSession: nextApt.date, nextSessionTime: nextApt.time } : { ...patient, nextSession: '', nextSessionTime: '' };

                // Last Session strictly from session history
                const latest = latestSession; // sessions already desc
                if (latest && latest.session_date) {
                  const localTime = new Date(latest.session_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  patient = { ...patient, lastSession: latest.session_date.slice(0, 10), lastSessionTime: localTime };
                }
              } catch (e) {
                console.warn('⚠️ Session status compute failed', e);
              }

              // Calculate weekly report from last 7 sessions with detailed emotion data
              const recentSessions = sessions.slice(0, 7);
              if (recentSessions.length > 0) {
                sessionData.weeklyReport = recentSessions.reverse().map((session, index) => {
                  if (session.emotion) {
                    const emotions = session.emotion;
                    // Calculate Overall Mental Health Score
                    const joy = Number(emotions.joy) || 0;
                    const stress = Number(emotions.stress) || 0;
                    const sadness = Number(emotions.sadness) || 0;
                    const anger = Number(emotions.anger) || 0;
                    const hurt = Number(emotions.hurt) || 0;
                    const burnout = Number(emotions.burnout_score) || 0;

                    // If all emotions are 0, return 0 (no data)
                    let overallScore = 0;
                    if (joy !== 0 || stress !== 0 || sadness !== 0 || anger !== 0 || hurt !== 0 || burnout !== 0) {
                      const positiveWeight = joy * 0.4;
                      const negativeWeight = (stress + sadness + anger + hurt + burnout) * 0.12;
                      overallScore = Math.max(0, Math.min(100, Math.round(50 + positiveWeight - negativeWeight)));
                    }

                    return {
                      score: overallScore,
                      emotions: {
                        joy: emotions.joy || 0,
                        stress: emotions.stress || 0,
                        sadness: emotions.sadness || 0,
                        anger: emotions.anger || 0,
                        hurt: emotions.hurt || 0,
                        burnout_score: emotions.burnout_score || 0
                      },
                      sessionDate: session.session_date,
                      sessionId: session.id
                    };
                  }
                  return {
                    score: 0,
                    emotions: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0 },
                    sessionDate: null,
                    sessionId: null
                  };
                });

                // Pad with defaults if less than 7 sessions - show 0% for days with no data
                while (sessionData.weeklyReport.length < 7) {
                  sessionData.weeklyReport.unshift({
                    score: 0,
                    emotions: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0 },
                    sessionDate: null,
                    sessionId: null
                  });
                }
                sessionData.hasWeeklyData = true;
              } else {
                // No sessions - mark as no data
                sessionData.weeklyReport = [];
                sessionData.hasWeeklyData = false;
              }

              // Calculate monthly report (group by month) with detailed emotion data
              const monthlyData = {};
              if (sessions && sessions.length > 0) {
                sessions.forEach(session => {
                  if (session.session_date && session.emotion) {
                    const month = session.session_date.substring(0, 7); // YYYY-MM
                    if (!monthlyData[month]) {
                      monthlyData[month] = [];
                    }
                    const emotions = session.emotion;
                    // Calculate Overall Mental Health Score
                    const joy = Number(emotions.joy) || 0;
                    const stress = Number(emotions.stress) || 0;
                    const sadness = Number(emotions.sadness) || 0;
                    const anger = Number(emotions.anger) || 0;
                    const hurt = Number(emotions.hurt) || 0;
                    const burnout = Number(emotions.burnout_score) || 0;

                    // If all emotions are 0, return 0 (no data)
                    let overallScore = 0;
                    if (joy !== 0 || stress !== 0 || sadness !== 0 || anger !== 0 || hurt !== 0 || burnout !== 0) {
                      const positiveWeight = joy * 0.4;
                      const negativeWeight = (stress + sadness + anger + hurt + burnout) * 0.12;
                      overallScore = Math.max(0, Math.min(100, Math.round(50 + positiveWeight - negativeWeight)));
                    }

                    monthlyData[month].push({
                      score: overallScore,
                      emotions: {
                        joy: emotions.joy || 0,
                        stress: emotions.stress || 0,
                        sadness: emotions.sadness || 0,
                        anger: emotions.anger || 0,
                        hurt: emotions.hurt || 0,
                        burnout_score: emotions.burnout_score || 0
                      },
                      sessionDate: session.session_date,
                      sessionId: session.id
                    });
                  }
                });
              }

              // Get last 4 months of data
              const monthKeys = Object.keys(monthlyData).sort().slice(-4);
              if (monthKeys.length > 0) {
                sessionData.monthlyReport = monthKeys.map(month => {
                  const sessions = monthlyData[month];
                  const avgScore = Math.round(sessions.reduce((sum, s) => sum + s.score, 0) / sessions.length);
                  const avgEmotions = sessions.reduce((acc, s) => {
                    acc.joy += s.emotions.joy;
                    acc.stress += s.emotions.stress;
                    acc.sadness += s.emotions.sadness;
                    acc.anger += s.emotions.anger;
                    acc.hurt += s.emotions.hurt;
                    return acc;
                  }, { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0 });

                  Object.keys(avgEmotions).forEach(key => {
                    avgEmotions[key] = Math.round(avgEmotions[key] / sessions.length);
                  });

                  return {
                    score: avgScore,
                    emotions: avgEmotions,
                    month: month,
                    sessionCount: sessions.length
                  };
                });

                // Pad with defaults if less than 4 months - show 0% for months with no data
                while (sessionData.monthlyReport.length < 4) {
                  sessionData.monthlyReport.unshift({
                    score: 0,
                    emotions: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0 },
                    month: null,
                    sessionCount: 0
                  });
                }
                sessionData.hasMonthlyData = true;
              } else {
                // No monthly data
                sessionData.monthlyReport = [];
                sessionData.hasMonthlyData = false;
              }

              // Additional risk calculations based on session trends (if not already set above)
              if (!sessionData.riskComponents.emotional_anomaly) {
                const avgBurnout = sessions.slice(0, 5).reduce((sum, session) => {
                  if (session.emotion) {
                    const burnout = session.emotion.burnout_score || 0;
                    return sum + burnout;
                  }
                  return sum;
                }, 0) / Math.min(5, sessions.length);

                sessionData.riskComponents = {
                  emotional_anomaly: Math.min(100, avgBurnout * 0.8),
                  behavioral_deviation: Math.min(100, avgBurnout * 0.6),
                  burnout: Math.round(avgBurnout),
                  intent_score: Math.min(100, avgBurnout * 0.4)
                };

                // Calculate predictions based on trends
                const recentBurnouts = sessions.slice(0, 7).map(s =>
                  s.emotion ? (s.emotion.burnout_score || 0) : 0
                );
                const avgRecentBurnout = recentBurnouts.reduce((sum, b) => sum + b, 0) / recentBurnouts.length;

                sessionData.predictions = {
                  suicide_risk: Math.min(100, Math.round(avgRecentBurnout * 0.8)),
                  violence_risk: Math.min(100, Math.round(avgRecentBurnout * 0.6))
                };
              }
            }
          } catch (sessionError) {
            console.error('Error loading session data for patient:', patient.id, sessionError);
            // Keep default values for new patients
          }

          return {
            id: patient.id,
            name: patient.name,
            age: patient.age,
            address: patient.address || '',
            contact: patient.contact || '',
            lastSession: patient.lastSession || '',
            lastSessionTime: patient.lastSessionTime || '',
            nextSession: patient.nextSession || '',
            nextSessionTime: patient.nextSessionTime || '',
            overallMark: sessionData.overallMark,
            lastMentalHealth: sessionData.lastMentalHealth,
            highRisk: sessionData.lastMentalHealth === 'High Risk',
            emotionVector: sessionData.emotionVector,
            weeklyReport: sessionData.weeklyReport,
            monthlyReport: sessionData.monthlyReport,
            predictions: sessionData.predictions,
            riskComponents: sessionData.riskComponents,
            hasSessionData: sessionData.hasSessionData
          };
        }));

        setPatients(transformedPatients);

        // --- Compute dashboard stats and growth from collected sessions ---
        try {
          const today = new Date();
          const toDateKey = (d) => d.toISOString().slice(0, 10);
          const startOfWeek = (d) => { const x = new Date(d); const day = x.getDay(); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - day); return x; };
          const startOfPrevWeek = (d) => { const s = startOfWeek(d); s.setDate(s.getDate() - 7); return s; };
          const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
          const startOfPrevMonth = (d) => new Date(d.getFullYear(), d.getMonth() - 1, 1);

          // Today sessions count
          const todayCount = allSessions.filter(s => toDateKey(s.date) === toDateKey(today)).length;

          // Weekly growth: compare current week vs previous week
          const sw = startOfWeek(today);
          const epw = new Date(sw); epw.setDate(epw.getDate() + 7);
          const spw = startOfPrevWeek(today);
          const eprev = new Date(spw); eprev.setDate(eprev.getDate() + 7);
          const currWeek = allSessions.filter(s => s.date >= sw && s.date < epw).length;
          const prevWeek = allSessions.filter(s => s.date >= spw && s.date < eprev).length;
          const weeklyGrowth = prevWeek > 0 ? Math.round(((currWeek - prevWeek) / prevWeek) * 100) : (currWeek > 0 ? 100 : 0);

          // Monthly growth: current vs previous month
          const sm = startOfMonth(today);
          const em = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          const spm = startOfPrevMonth(today);
          const epm = new Date(today.getFullYear(), today.getMonth(), 1);
          const currMonth = allSessions.filter(s => s.date >= sm && s.date < em).length;
          const prevMonth = allSessions.filter(s => s.date >= spm && s.date < epm).length;
          const monthlyGrowth = prevMonth > 0 ? Math.round(((currMonth - prevMonth) / prevMonth) * 100) : (currMonth > 0 ? 100 : 0);

          setDashboardStats({
            totalPatients: transformedPatients.length,
            highRiskPatients: transformedPatients.filter(p => p.highRisk).length,
            todaySessions: todayCount,
            weeklyGrowth,
            monthlyGrowth
          });

          // Daily chart: last 7 days
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const last7 = Array.from({ length: 7 }).map((_, i) => { const d = new Date(today); d.setDate(d.getDate() - (6 - i)); d.setHours(0, 0, 0, 0); return d; });
          const daily = last7.map(d => {
            const next = new Date(d); next.setDate(d.getDate() + 1);
            const sessionsCount = allSessions.filter(s => s.date >= d && s.date < next).length;
            const patientsCount = new Set(allSessions.filter(s => s.date >= d && s.date < next).map(s => s.patientId)).size;
            return { day: days[d.getDay()], patients: patientsCount, sessions: sessionsCount };
          });

          // Weekly chart: last 4 weeks
          const weekStarts = Array.from({ length: 4 }).map((_, i) => { const d = startOfWeek(today); d.setDate(d.getDate() - ((3 - i) * 7)); return d; });
          const weekly = weekStarts.map((ws, idx) => {
            const we = new Date(ws); we.setDate(ws.getDate() + 7);
            const sess = allSessions.filter(s => s.date >= ws && s.date < we);
            const patientsCount = new Set(sess.map(s => s.patientId)).size;
            return { week: `Week ${idx + 1}`, patients: patientsCount, sessions: sess.length };
          });

          setGrowthData({ daily, weekly, monthly: [] });
        } catch (e) {
          console.warn('⚠️ Unable to compute dashboard charts', e);
        }
      }
    } catch (error) {
      console.error('Error loading patients:', error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const processedPatients = useMemo(() =>
    patients.map(loadPatientDashboard),
    [patients]
  );

  // Allow Appointments list to trigger opening a session for outpatient video call
  useEffect(() => {
    function handleOpenDoctorSession(ev) {
      try {
        const pid = ev?.detail?.patientId;
        const roomId = ev?.detail?.roomId;
        const outpatientUserId = ev?.detail?.outpatientUserId;
        if (!pid) return;
        const p = processedPatients.find(x => x.id === pid) || patients.find(x => x.id === pid);
        if (p) {
          setSessionPatient(p);
          setShowVideoCall(true);
          if (roomId) {
            // Pass room id via window for VideoCallModal
            try { window.__VIDEO_ROOM_ID__ = roomId; } catch { }
          }
          if (outpatientUserId) {
            try { window.__VIDEO_OUTPATIENT_UID__ = outpatientUserId; } catch { }
          }
        }
      } catch { }
    }
    window.addEventListener('openDoctorSession', handleOpenDoctorSession);
    return () => window.removeEventListener('openDoctorSession', handleOpenDoctorSession);
  }, [processedPatients, patients]);

  const highRiskPatients = processedPatients.filter(p => p.highRisk);
  const nextSession = processedPatients
    .filter(p => p.nextSession)
    .sort((a, b) => new Date(a.nextSession) - new Date(b.nextSession))[0];

  const lastSession = processedPatients
    .filter(p => p.lastSession)
    .sort((a, b) => new Date(b.lastSession) - new Date(a.lastSession))[0];

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    setShowPatientModal(true);
  };

  const handleReportClick = async (patient) => {
    setSelectedPatient(patient);
    // Load session history for trend analysis
    await loadSessionHistory(patient.id);
    setShowReportModal(true);
  };

  const handleOpenSession = (patient) => {
    setSessionPatient(patient);
    setShowSessionModal(true);
    setAnalyzedEmotion(null);
    setSessionNote('');
  };

  const handleViewSessionHistory = async (patient) => {
    setSelectedPatient(patient);
    await loadSessionHistory(patient.id);
    setShowSessionHistory(true);
  };

  const handleViewSessionDetails = async (session) => {
    setSelectedSessionDetails(session);
    await loadSessionNotes(session.session_id);
    setShowSessionDetailsModal(true);
  };

  const openAddPatient = () => {
    setAddForm({
      name: '', age: '', address: '', contact: ''
    });
    setShowAddModal(true);
  };

  const validateAddForm = () => {
    if (!addForm.name.trim()) return 'Name is required';
    if (!addForm.contact.trim()) return 'Contact is required';
    return '';
  };

  const validateEditForm = () => {
    if (!editForm.name.trim()) return 'Name is required';
    if (!editForm.contact.trim()) return 'Contact is required';
    return '';
  };

  const handleSavePatient = async (e) => {
    e.preventDefault();
    const error = validateAddForm();
    if (error) {
      alert(error);
      return;
    }

    try {
      const response = await doctorCreatePatient(
        doctorId,
        addForm.name.trim(),
        addForm.age ? parseInt(addForm.age, 10) : null,
        addForm.address || '',
        addForm.contact.trim(),
        null, // lastSession
        null, // nextSession
        'No Data'
      );

      if (response.status_code === 200) {
        // Reload patients from database
        await loadPatients(doctorId);
        setShowAddModal(false);
        alert('Patient added successfully');
      } else {
        alert('Failed to add patient. Please try again.');
      }
    } catch (error) {
      console.error('Error adding patient:', error);
      alert('Failed to add patient. Please try again.');
    }
  };

  const handleDeletePatient = async (patient) => {
    // Show confirmation dialog
    const confirmed = (typeof window !== 'undefined' && window.confirm) ? window.confirm(
      `Are you sure you want to delete patient "${patient.name}"?\n\nThis action will permanently delete:\n- Patient information\n- All session data\n- All emotion analysis results\n- All session notes\n\nThis action cannot be undone.`
    ) : false;

    if (!confirmed) {
      return;
    }

    try {
      const response = await doctorDeletePatient(patient.id);

      if (response.status_code === 200) {
        // Reload patients from database
        await loadPatients(doctorId);
        alert(`Patient "${patient.name}" has been deleted successfully.`);
      } else {
        alert('Failed to delete patient. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting patient:', error);
      alert('Failed to delete patient. Please try again.');
    }
  };

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setEditForm({
      name: patient.name || '',
      age: patient.age || '',
      address: patient.address || '',
      contact: patient.contact || ''
    });
    setShowEditModal(true);
  };

  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    const error = validateEditForm();
    if (error) {
      alert(error);
      return;
    }

    try {
      const response = await doctorUpdatePatient(
        editingPatient.id,
        editForm.name.trim(),
        editForm.age ? parseInt(editForm.age, 10) : null,
        editForm.address || '',
        editForm.contact.trim(),
        null, // lastSession
        null, // nextSession
        editingPatient.lastMentalHealth || 'No Data'
      );

      if (response.status_code === 200) {
        // Reload patients from database
        await loadPatients(doctorId);
        setShowEditModal(false);
        setEditingPatient(null);
        alert('Patient updated successfully');
      } else {
        alert('Failed to update patient. Please try again.');
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      alert('Failed to update patient. Please try again.');
    }
  };

  const DashboardView = () => (
    <div className="doctor-dashboard">
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{t('Total-Patients')}</h3>
          <div className="stat-value">{patients.length}</div>
        </div>
        <div className="stat-card">
          <h3>{t('High Risk Patients')}</h3>
          <div className="stat-value high-risk">{highRiskPatients.length}</div>
        </div>
        <div className="stat-card">
          <h3>{t("Today's Sessions")}</h3>
          <div className="stat-value">{dashboardStats.todaySessions}</div>
        </div>
        <div className="stat-card">
          <h3>{t('Weekly Growth')}</h3>
          <div className="stat-value positive">+{dashboardStats.weeklyGrowth}%</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3>{t('Daily Growth')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={growthData.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="patients" fill="#8884d8" />
              <Bar dataKey="sessions" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>{t('Weekly Growth')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={growthData.weekly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="patients" stroke="#8884d8" />
              <Line type="monotone" dataKey="sessions" stroke="#82ca9d" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sessions Info */}
      <div className="sessions-info">
        <div className="session-card">
          <h3>{t('Last Session')}</h3>
          {lastSession ? (
            <div>
              <p><strong>{lastSession.name}</strong></p>
              <p>{lastSession.lastSession}</p>
              <p className={`status ${lastSession.healthStatus.toLowerCase().replace(' ', '-')}`}>
                {lastSession.healthStatus}
              </p>
            </div>
          ) : (
            <p>{t('No recent sessions')}</p>
          )}
        </div>

        <div className="session-card">
          <h3>{t('Next Session')}</h3>
          {nextSession ? (
            <div>
              <p><strong>{nextSession.name}</strong></p>
              <p>{nextSession.nextSession}</p>
              <p className={`status ${nextSession.healthStatus.toLowerCase().replace(' ', '-')}`}>
                {nextSession.healthStatus}
              </p>
            </div>
          ) : (
            <p>{t('No upcoming sessions')}</p>
          )}
        </div>
      </div>

      {/* High Risk Patients */}
      <div className="high-risk-section">
        <h3>{t('High Risk Patients')}</h3>
        <div className="patients-grid">
          {highRiskPatients.map(patient => (
            <div
              key={patient.id}
              className={`patient-card ${patient.highRisk ? 'high-risk' : ''}`}
              onClick={() => handlePatientClick(patient)}
            >
              <h4>{patient.name}</h4>
              <p>Age: {patient.age}</p>
              <p>Risk Score: {Math.round(patient.cumulativeRisk)}%</p>
              <p className="mental-health">{patient.lastMentalHealth}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const PatientsView = () => (
    <div className="patients-view">
      <div className="patients-header">
        <h2>{t('All Patients')}</h2>
        <button className="add-patient-btn" onClick={openAddPatient}>+ {t('Add Patient')}</button>
        <button className="add-patient-btn" onClick={exportPatientsToCSV} title={t('Export all patients to CSV')}>📥 {t('Export')}</button>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading patients...</div>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="patients-table">
            <table>
              <thead>
                <tr>
                  <th>{t('Name')}</th>
                  <th>{t('Age')}</th>
                  <th>{t('Address')}</th>
                  <th>{t('Contact')}</th>
                  <th>{t('Last Session')}</th>
                  <th>{t('Next Session')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {processedPatients.map(patient => (
                  <tr key={patient.id} className={patient.highRisk ? 'high-risk-row' : ''}>
                    <td>{patient.name}</td>
                    <td>{patient.age}</td>
                    <td>{patient.address}</td>
                    <td>{patient.contact}</td>
                    <td>
                      {patient.lastSession
                        ? `${patient.lastSession}${patient.lastSessionTime ? `, ${patient.lastSessionTime}` : ''}`
                        : 'No previous session'}
                    </td>
                    <td>
                      {patient.nextSession
                        ? `${patient.nextSession}${patient.nextSessionTime ? `, ${patient.nextSessionTime}` : ''}`
                        : 'Not Scheduled'}
                    </td>
                    <td>
                      <span className={`status ${patient.healthStatus.toLowerCase().replace(' ', '-')}`}>
                        {patient.healthStatus}
                      </span>
                    </td>
                    <td>
                      <button
                        className="report-btn"
                        onClick={() => handleReportClick(patient)}
                      >
                        {t('Report')}
                      </button>
                      <button
                        className="report-btn"
                        style={{ marginLeft: 8 }}
                        onClick={() => handleOpenSession(patient)}
                      >
                        {t('Add Session')}
                      </button>
                      <button
                        className="report-btn"
                        style={{ marginLeft: 8 }}
                        onClick={() => handleViewSessionHistory(patient)}
                      >
                        {t('History')}
                      </button>
                      <button
                        className="report-btn edit-btn"
                        style={{ marginLeft: 8, backgroundColor: '#28a745', color: 'white' }}
                        onClick={() => handleEditPatient(patient)}
                      >
                        {t('Edit')}
                      </button>
                      <button
                        className="report-btn delete-btn"
                        style={{ marginLeft: 8, backgroundColor: '#dc3545', color: 'white' }}
                        onClick={() => handleDeletePatient(patient)}
                      >
                        {t('Delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="patients-mobile-cards">
            {processedPatients.map(patient => (
              <div
                key={patient.id}
                className={`patient-mobile-card ${patient.highRisk ? 'high-risk' : ''}`}
                onClick={() => handlePatientClick(patient)}
              >
                <div className="patient-name">{patient.name}</div>
                <div className="patient-details">
                  <div className="patient-detail">
                    <span className="label">{t('Age')}:</span>
                    <span className="value">{patient.age || 'Not specified'}</span>
                  </div>
                  <div className="patient-detail">
                    <span className="label">{t('Address')}:</span>
                    <span className="value">{patient.address || 'Not provided'}</span>
                  </div>
                  <div className="patient-detail">
                    <span className="label">{t('Contact')}:</span>
                    <span className="value">{patient.contact || 'Not provided'}</span>
                  </div>
                  <div className="patient-detail">
                    <span className="label">{t('Last Session')}:</span>
                    <span className="value">
                      {patient.lastSession
                        ? `${patient.lastSession}${patient.lastSessionTime ? `, ${patient.lastSessionTime}` : ''}`
                        : 'No previous session'}
                    </span>
                  </div>
                  <div className="patient-detail">
                    <span className="label">{t('Next Session')}:</span>
                    <span className="value">
                      {patient.nextSession
                        ? `${patient.nextSession}${patient.nextSessionTime ? `, ${patient.nextSessionTime}` : ''}`
                        : 'Not Scheduled'}
                    </span>
                  </div>
                </div>
                <div className="patient-status">
                  <span className={`status ${patient.healthStatus.toLowerCase().replace(' ', '-')}`}>
                    {patient.healthStatus}
                  </span>
                </div>
                <div className="patient-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="action-btn report-btn"
                    onClick={() => handleReportClick(patient)}
                  >
                    {t('Report')}
                  </button>
                  <button
                    className="action-btn report-btn"
                    onClick={() => handleOpenSession(patient)}
                  >
                    {t('Add Session')}
                  </button>
                  <button
                    className="action-btn report-btn"
                    onClick={() => handleViewSessionHistory(patient)}
                  >
                    {t('History')}
                  </button>
                  <button
                    className="action-btn edit-btn"
                    onClick={() => handleEditPatient(patient)}
                  >
                    {t('Edit')}
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDeletePatient(patient)}
                  >
                    {t('Delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const PatientModal = () => (
    <div className="modal-overlay" onClick={() => setShowPatientModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('Patient Details')} - {selectedPatient?.name}</h2>
          <button className="close-btn" onClick={() => setShowPatientModal(false)}>×</button>
        </div>
        <div className="modal-body">
          <div className="patient-info">
            <p><strong>{t('Name')}:</strong> {selectedPatient?.name}</p>
            <p><strong>{t('Age')}:</strong> {selectedPatient?.age}</p>
            <p><strong>{t('Address')}:</strong> {selectedPatient?.address}</p>
            <p><strong>{t('Contact')}:</strong> {selectedPatient?.contact}</p>
            <p><strong>{t('Last Mental Health')}:</strong> {selectedPatient?.lastMentalHealth}</p>
            <p><strong>{t('Overall Mark')}:</strong> {selectedPatient?.overallMark}%</p>
            <p><strong>{t('Risk Score')}:</strong> {Math.round(selectedPatient?.cumulativeRisk || 0)}%</p>
            <p><strong>{t('Last Session')}:</strong> {selectedPatient?.lastSession}</p>
            <p><strong>{t('Next Session')}:</strong> {selectedPatient?.nextSession}</p>
          </div>
        </div>
      </div>
    </div>
  );


  const SessionHistoryModal = () => (
    <div className="modal-overlay" onClick={() => setShowSessionHistory(false)}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Session History - {selectedPatient?.name}</h2>
          <button className="close-btn" onClick={() => setShowSessionHistory(false)}>×</button>
        </div>
        <div className="modal-body">
          {sessionHistory.length === 0 ? (
            <p>No sessions found for this patient.</p>
          ) : (
            <div className="session-history-table-container">
              <table className="session-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Duration</th>
                    <th>Joy</th>
                    <th>Stress</th>
                    <th>Sadness</th>
                    <th>Anger</th>
                    <th>Burnout</th>
                    <th>Assessment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionHistory.map(session => (
                    <tr key={session.session_id}>
                      <td>{session.session_date ? new Date(session.session_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{session.duration_seconds ? `${Math.round(session.duration_seconds / 60)}m` : 'N/A'}</td>
                      <td>{session.emotion ? `${Math.round(session.emotion.joy)}%` : 'N/A'}</td>
                      <td>{session.emotion ? `${Math.round(session.emotion.stress)}%` : 'N/A'}</td>
                      <td>{session.emotion ? `${Math.round(session.emotion.sadness)}%` : 'N/A'}</td>
                      <td>{session.emotion ? `${Math.round(session.emotion.anger)}%` : 'N/A'}</td>
                      <td>{session.emotion ? `${Math.round(session.emotion.burnout_score)}%` : 'N/A'}</td>
                      <td>
                        {session.emotion && session.emotion.doctor_assessment ? (
                          <span className="assessment-badge-small">
                            {session.emotion.doctor_assessment === 'Stable' && '🟢 Stable'}
                            {session.emotion.doctor_assessment === 'Moderate Risk' && '🟠 Moderate Risk'}
                            {session.emotion.doctor_assessment === 'High Risk' && '🔴 High Risk'}
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td>
                        <button
                          className="report-btn"
                          onClick={() => handleViewSessionDetails(session)}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const SessionDetailsModal = () => (
    <div className="modal-overlay" onClick={() => setShowSessionDetailsModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Session Details</h2>
          <button className="close-btn" onClick={() => setShowSessionDetailsModal(false)}>×</button>
        </div>
        <div className="modal-body">
          {selectedSessionDetails && (
            <div className="session-details">
              <div className="detail-row">
                <strong>Date:</strong> {selectedSessionDetails.session_date ? new Date(selectedSessionDetails.session_date).toLocaleString() : 'N/A'}
              </div>
              <div className="detail-row">
                <strong>Duration:</strong> {selectedSessionDetails.duration_seconds ? `${Math.round(selectedSessionDetails.duration_seconds / 60)} minutes` : 'N/A'}
              </div>
              {selectedSessionDetails.emotion && (
                <div className="emotion-details">
                  <h3>Emotion Analysis</h3>
                  <div className="emotion-grid">
                    <div className="emotion-item">
                      <span>Joy:</span> <span>{Math.round(selectedSessionDetails.emotion.joy)}%</span>
                    </div>
                    <div className="emotion-item">
                      <span>Stress:</span> <span>{Math.round(selectedSessionDetails.emotion.stress)}%</span>
                    </div>
                    <div className="emotion-item">
                      <span>Sadness:</span> <span>{Math.round(selectedSessionDetails.emotion.sadness)}%</span>
                    </div>
                    <div className="emotion-item">
                      <span>Anger:</span> <span>{Math.round(selectedSessionDetails.emotion.anger)}%</span>
                    </div>
                    <div className="emotion-item highlight">
                      <span>Burnout Score:</span> <span>{Math.round(selectedSessionDetails.emotion.burnout_score)}%</span>
                    </div>
                  </div>
                </div>
              )}
              {selectedSessionDetails.emotion && selectedSessionDetails.emotion.doctor_assessment && (
                <div className="clinical-assessment-display">
                  <h3>Clinical Assessment by Doctor</h3>
                  <div className="assessment-badge">
                    {selectedSessionDetails.emotion.doctor_assessment === 'Stable' && '🟢 Stable'}
                    {selectedSessionDetails.emotion.doctor_assessment === 'Moderate Risk' && '🟠 Moderate Risk'}
                    {selectedSessionDetails.emotion.doctor_assessment === 'High Risk' && '🔴 High Risk'}
                  </div>
                </div>
              )}
              <div className="session-notes">
                <h3>Session Notes</h3>
                {sessionNotes.length === 0 ? (
                  <p>No notes for this session.</p>
                ) : (
                  <div className="notes-list">
                    {sessionNotes.map(note => (
                      <div key={note.note_id} className="note-item">
                        <div className="note-text">{note.note_text}</div>
                        <div className="note-meta">
                          {note.created_at ? new Date(note.created_at).toLocaleString() : 'Unknown time'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <AppShell
      role="doctor"
      className="doctor-dashboard-container"
      workspace={t('Clinical workspace')}
      title={t('Doctor Dashboard')}
      subtitle={t('Patients, risk and scheduled sessions in one place')}
      nav={[
        { key: 'dashboard', label: t('Overview'), shortLabel: t('Overview'), icon: <LuLayoutDashboard /> },
        { key: 'patients', label: t('Patients'), shortLabel: t('Patients'), icon: <LuUsers /> },
        {
          key: 'appointments',
          label: t('Appointments'),
          shortLabel: t('Appointments'),
          icon: <LuCalendarDays />,
          badge: newAppointmentIds.size > 0 ? newAppointmentIds.size : null,
        },
      ]}
      activeKey={activeTab}
      onNavigate={setActiveTab}
      actions={
        <button type="button" className="xi-btn xi-btn--primary xi-btn--sm" onClick={() => setShowAddModal(true)}>
          <LuPlus aria-hidden="true" />
          <span className="xi-hide-sm">{t('Add patient')}</span>
        </button>
      }
    >
      {/* Content */}
      <div className="dashboard-content">
        {activeTab === 'dashboard' && <DashboardView />}
        {activeTab === 'patients' && <PatientsView />}
        {activeTab === 'appointments' && (
          <Appointments
            newAppointmentIds={newAppointmentIds}
            setNewAppointmentIds={setNewAppointmentIds}
            onAppointmentCreated={() => {
              if (doctorId) {
                // Re-fetch patients so Next Session column updates instantly
                loadPatients(doctorId);
              }
            }}
          />
        )}
      </div>

      {/* Modals */}
      {showPatientModal && <PatientModal />}
      {showReportModal && (
        <ReportModal
          patient={selectedPatient}
          onClose={() => setShowReportModal(false)}
          onStartSession={() => handleOpenSession(selectedPatient)}
          sessionHistory={sessionHistory}
          latestSessionNote={latestSessionNote}
        />
      )}
      {showSessionHistory && <SessionHistoryModal />}
      {showSessionDetailsModal && <SessionDetailsModal />}
      {showSessionModal && (
        <SessionModal
          patient={sessionPatient}
          onClose={() => {
            setShowSessionModal(false);
          }}
          onSessionSaved={() => {
            // Refresh patient data after session is saved
            if (doctorId) {
              loadPatients(doctorId);
            }
          }}
        />
      )}
      {showVideoCall && (
        <VideoCallModal
          doctorId={doctorId}
          patient={sessionPatient}
          onClose={() => setShowVideoCall(false)}
          onSessionSaved={() => { if (doctorId) loadPatients(doctorId); }}
        />
      )}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Patient</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form className="add-patient-form" onSubmit={handleSavePatient}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Age</label>
                    <input type="number" min="0" value={addForm.age} onChange={(e) => setAddForm({ ...addForm, age: e.target.value })} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <textarea rows="2" value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })}></textarea>
                </div>

                <div className="form-group">
                  <label>Contact *</label>
                  <input type="tel" value={addForm.contact} onChange={(e) => setAddForm({ ...addForm, contact: e.target.value })} required />
                </div>


                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Patient - {editingPatient?.name}</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form className="add-patient-form" onSubmit={handleUpdatePatient}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Age</label>
                    <input type="number" min="0" value={editForm.age} onChange={(e) => setEditForm({ ...editForm, age: e.target.value })} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <textarea rows="2" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}></textarea>
                </div>

                <div className="form-group">
                  <label>Contact *</label>
                  <input type="tel" value={editForm.contact} onChange={(e) => setEditForm({ ...editForm, contact: e.target.value })} required />
                </div>


                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Update</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
